import { splitTextByChannelMention } from './channelMention';

export type EditableSegment =
  | {
      type: 'text';
      value: string;
      start: number;
      end: number;
    }
  | {
      type: 'mention';
      value: string;
      start: number;
      end: number;
    }
  | {
      type: 'link';
      value: { name: string; href: string };
      start: number;
      end: number;
    };

const linkRegex = /\[([^[\]]+)\]\(([^()\s]+)\)/g;

export const parseEditable = (value: string): EditableSegment[] => {
  const segments: EditableSegment[] = [];
  const mentionParts = splitTextByChannelMention(value);
  let cursor = 0;

  mentionParts.forEach((segment, idx) => {
    if (segment.isChannelMention) {
      if (segments.length === 0 && cursor === 0) {
        segments.push({ type: 'text', value: '', start: 0, end: 0 });
      }
      segments.push({
        type: 'mention',
        value: segment.text,
        start: cursor,
        end: cursor + segment.text.length,
      });
      cursor += segment.text.length;
      return;
    }

    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = linkRegex.exec(segment.text)) !== null) {
      const [fullMatch, linkName, linkHref] = match;
      if (match.index > lastIndex) {
        segments.push({
          type: 'text',
          value: segment.text.slice(lastIndex, match.index),
          start: cursor + lastIndex,
          end: cursor + match.index,
        });
      } else if (match.index === 0 && segments.length === 0 && cursor === 0) {
        // ensure leading text segment even if empty
        segments.push({ type: 'text', value: '', start: cursor, end: cursor });
      }

      segments.push({
        type: 'link',
        value: { name: linkName, href: linkHref },
        start: cursor + match.index,
        end: cursor + match.index + fullMatch.length,
      });
      lastIndex = match.index + fullMatch.length;
    }

    // trailing text (possibly empty to allow caret after link)
    const trailing = segment.text.slice(lastIndex);
    segments.push({
      type: 'text',
      value: trailing,
      start: cursor + lastIndex,
      end: cursor + segment.text.length,
    });

    cursor += segment.text.length;
  });

  // coalesce consecutive text
  const coalesced: EditableSegment[] = [];
  segments.forEach((seg) => {
    const last = coalesced[coalesced.length - 1];
    if (last && last.type === 'text' && seg.type === 'text') {
      last.value += seg.value;
      last.end = seg.end;
    } else {
      coalesced.push({ ...seg });
    }
  });

  if (coalesced.length === 0) {
    coalesced.push({ type: 'text', value: '', start: 0, end: 0 });
  }

  return coalesced;
};

export const findSegmentAt = (
  segments: EditableSegment[],
  position: number,
): EditableSegment | null => {
  for (let i = 0; i < segments.length; i += 1) {
    const seg = segments[i];
    if (
      i > 0 &&
      position === seg.start &&
      segments[i - 1].type === 'text' &&
      segments[i - 1].start === segments[i - 1].end
    ) {
      return segments[i - 1];
    }
    if (seg.type === 'text' && seg.start === seg.end) {
      if (position === seg.start && i < segments.length - 1) {
        const next = segments[i + 1];
        if (position <= next.end) {
          return next;
        }
      }
      continue;
    }
    if (position < seg.start) continue;
    if (position < seg.end) {
      return seg;
    }
  }
  const last = segments[segments.length - 1];
  if (last && position === last.end) return last;
  return null;
};

export const renderedOffsetToMarkdownOffset = (
  segments: EditableSegment[],
  renderedOffset: number,
): number => {
  let renderedPos = 0;

  for (const seg of segments) {
    const renderedLength =
      seg.type === 'link' ? seg.value.name.length : seg.value.length;

    if (renderedOffset <= renderedPos + renderedLength) {
      const offsetInSeg = renderedOffset - renderedPos;
      if (seg.type === 'link') {
        // Name starts at seg.start + 1 in markdown ([name](href))
        if (offsetInSeg >= renderedLength) {
          return seg.end;
        }
        return seg.start + 1 + offsetInSeg;
      }
      return seg.start + offsetInSeg;
    }

    renderedPos += renderedLength;
  }

  const last = segments[segments.length - 1];
  return last ? last.end : renderedOffset;
};

export const markdownOffsetToRenderedOffset = (
  segments: EditableSegment[],
  mdOffset: number,
): number => {
  let renderedPos = 0;
  for (const seg of segments) {
    const mdLength =
      seg.type === 'link'
        ? `[${seg.value.name}](${seg.value.href})`.length
        : seg.value.length;
    const renderedLength =
      seg.type === 'link' ? seg.value.name.length : seg.value.length;

    if (mdOffset <= seg.start) {
      return renderedPos;
    }
    if (mdOffset >= seg.end) {
      renderedPos += renderedLength;
      continue;
    }

    const offsetInSeg = mdOffset - seg.start;
    if (seg.type === 'link') {
      // name starts after '['
      const nameStart = 1;
      const nameEnd = nameStart + seg.value.name.length;
      if (offsetInSeg <= nameStart) return renderedPos;
      if (offsetInSeg >= nameEnd) return renderedPos + seg.value.name.length;
      return renderedPos + (offsetInSeg - nameStart);
    }

    return renderedPos + offsetInSeg;
  }
  return renderedPos;
};
